import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MODULES } from "@/lib/modules";

// Trilha de navegacao no padrao Home / Secao / Pagina.
//
// A URL nem sempre reflete o caminho que a pessoa percorreu:
// o card "Mensagens e-CAC" fica em /app/mensagens mas leva
// para /app/serpro/caixa-postal. Por isso as relacoes de card
// ficam declaradas aqui; o resto sobe pela propria URL.

// Algumas telas trocam de secao sem trocar de rota (Mensagens,
// Certidoes). Elas publicam o pedaco final da trilha aqui, e o
// segmento da rota vira clicavel para voltar ao inicio da tela.
let trilhaExtra = { raiz: null, rotulo: "", itens: [], acoes: [] };
const ouvintes = new Set();

function publicar(valor) {
  trilhaExtra = valor;
  ouvintes.forEach((avisar) => avisar());
}

export function useTrilhaExtra(raiz, itens, rotulo, acoes) {
  const chave = (itens || []).filter(Boolean).join(" > ");
  const nome = rotulo || "";

  useEffect(() => {
    if (!chave) {
      publicar({ raiz: null, rotulo: "", itens: [], acoes: [] });
      return undefined;
    }
    publicar({
      raiz: raiz || null,
      rotulo: nome,
      itens: chave.split(" > "),
      acoes: acoes || [],
    });
    return () => publicar({ raiz: null, rotulo: "", itens: [], acoes: [] });
  }, [chave, nome]);
}

const PAI = {
  "/app/serpro/caixa-postal": "/app/mensagens",
  "/app/serpro/sitfis": "/app/dashboard-federal",
  "/app/simples-nacional": "/app/dashboard-federal",
  "/app/dctfweb": "/app/dashboard-federal",
  "/app/simples-nacional/das": "/app/dashboard-federal",
  "/app/simples-nacional/fator-r": "/app/dashboard-federal",
  "/app/obrigacoes": "/app/dashboard-federal",
  "/app/simples-nacional/apuracao": "/app", // SIMPLER_P15_2: Home / Simples Nacional
  "/app/integracoes/scheduler": "/app/integracoes",
  "/app/bi/ausencias": "/app/painel-360",
  "/app/bi/diagnosticos": "/app/painel-360",
  "/app/bi/indicadores": "/app/painel-360",
};

// Telas que sao cards do Dashboard Federal: entram com ele
// na frente, junto com qualquer rota filha delas.
const FILHOS_DO_FEDERAL = [
  "/app/serpro/sitfis",
  "/app/simples-nacional",
  "/app/dctfweb",
  "/app/dashboard-federal/pgdas",
  "/app/dashboard-federal/comprovantes",
  "/app/dashboard-federal/divida-ativa",
  "/app/procuradores",
];

function limparBarras(valor) {
  let saida = String(valor || "");
  while (saida.length > 1 && saida.endsWith("/")) {
    saida = saida.slice(0, -1);
  }
  return saida || "/";
}

function moduloDe(caminho) {
  for (const m of MODULES) {
    if (m.path === caminho) return m;
  }
  return null;
}

// Modulos que aparecem na trilha com outro nome.
const ROTULO = {
  "/app/mensagens": "Central de Mensagens",
  "/app/certidoes": "Tipos de Certidão",
  "/app/parcelamentos": "Tipos de Parcelamentos",
  "/app/dashboard-federal/comprovantes": "Comprovantes de Pagamento",
  "/app/dashboard-federal/comprovantes/filtro": "Filtro avançado",
  "/app/simples-nacional/das": "DAS",
  "/app/simples-nacional/fator-r": "Fator R",
  "/app/obrigacoes": "DEFIS",
};

function rotuloDe(caminho) {
  if (ROTULO[caminho]) return ROTULO[caminho];

  // Telas de detalhe com id na URL: o ultimo pedaco e um numero,
  // entao o nome vem daqui em vez de virar "30596646".
  if (/^\/app\/parcelamentos\/[0-9a-f]{24}$/i.test(caminho)) {
    return "Detalhes";
  }

  if (caminho.startsWith("/app/dashboard-federal/mit/")) {
    return "MIT - Detalhes";
  }

  const modulo = moduloDe(caminho);
  if (modulo) {
    return modulo.label || modulo.name || modulo.title;
  }

  const partes = caminho.split("/").filter(Boolean);
  const ultimo = partes.length ? partes[partes.length - 1] : "";
  const texto = String(ultimo).split("-").join(" ");
  if (!texto) return caminho;
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function paiDe(caminho) {
  if (PAI[caminho]) return PAI[caminho];

  for (const raiz of FILHOS_DO_FEDERAL) {
    if (caminho === raiz) return "/app/dashboard-federal";
    if (caminho.startsWith(raiz + "/")) return raiz;
  }

  const partes = caminho.split("/").filter(Boolean);
  for (let i = partes.length - 1; i > 1; i--) {
    const tentativa = "/" + partes.slice(0, i).join("/");
    if (moduloDe(tentativa)) return tentativa;
  }
  return null;
}

export default function Breadcrumb() {
  const location = useLocation();
  const caminho = limparBarras(location.pathname);
  const [extra, setExtra] = useState(trilhaExtra);

  // H17: na página de análise, o fim da trilha é a aba selecionada
  const abaRotulo =
    caminho === "/app/health-score"
      ? {
          health: "Saúde Fiscal",
          pre: "Triagem Simpler",
          post: "Evolução Simpler",
          simulator: "Simulador Tributário",
          diagnostic: "Diagnóstico",
        }[new URLSearchParams(location.search).get("tab") || "health"] || "Saúde Fiscal"
      : null;

  useEffect(() => {
    const avisar = () => setExtra(trilhaExtra);
    ouvintes.add(avisar);
    avisar();
    return () => ouvintes.delete(avisar);
  }, []);

  if (caminho === "/app" || caminho === "/app/dashboard") return null;
  if (!caminho.startsWith("/app/")) return null;

  const cadeia = [caminho];
  let atual = caminho;

  for (let i = 0; i < 4; i++) {
    const pai = paiDe(atual);
    if (!pai || pai === "/app" || cadeia.includes(pai)) break;
    cadeia.unshift(pai);
    atual = pai;
  }

  const separador = (
    <span className="select-none text-muted-foreground/50">/</span>
  );

  return (
    <nav
      aria-label="Trilha de navegacao"
      className="mb-3 flex flex-wrap items-center gap-2 text-[13px]"
    >
      <Link
        to="/app"
        className="font-medium text-primary transition-colors hover:underline"
      >
        Home
      </Link>

      {cadeia.map((item, indice) => {
        const fimDaRota = indice === cadeia.length - 1;
        const ultimo = fimDaRota && extra.itens.length === 0;

        return (
          <span key={item} className="flex items-center gap-2">
            {separador}
            {ultimo ? (
              <span className="text-muted-foreground">
                {abaRotulo && item === caminho ? abaRotulo : rotuloDe(item)}
              </span>
            ) : fimDaRota && extra.raiz ? (
              <button
                type="button"
                onClick={extra.raiz}
                className="font-medium text-primary transition-colors hover:underline"
              >
                {extra.rotulo || rotuloDe(item)}
              </button>
            ) : (
              <Link
                to={item}
                className="font-medium text-primary transition-colors hover:underline"
              >
                {rotuloDe(item)}
              </Link>
            )}
          </span>
        );
      })}

      {extra.itens.map((texto, i) => {
        const acao = (extra.acoes || [])[i];
        return (
          <span key={texto + i} className="flex items-center gap-2">
            {separador}
            {acao && i < extra.itens.length - 1 ? (
              <button type="button" onClick={acao} className="font-medium text-primary transition-colors hover:underline">{texto}</button>
            ) : (
              <span className="text-muted-foreground">{texto}</span>
            )}
          </span>
        );
      })}
    </nav>
  );
}
